import React, { useMemo } from 'react';

import config from 'config';
import snakeCase from 'lodash/snakeCase';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { createDataAutoTagString } from '~/shared/utils/createDataAutoTagString';
import { getSizeChartDataAllCollections } from '~/shared/utils/sizeConstants';
import {
  Button,
  Collapsible,
  desktop,
  mobile,
} from '~/techstyle-shared/react-components';

import { useSizeSelectionContext } from '../context';

const Content = styled.div`
  ${mobile`
    display: flex;
    justify-content: center;
  `}

  ${({ theme }) =>
    theme.sizeSelectionExpansionPanel.sizeSelectionContent.content};
`;

const SizeBlock = styled(Button).attrs({ variant: 'unstyled' })`
  font-size: 16px;
  font-family: inherit;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.02em;
  text-align: center;
  background: none;
  cursor: pointer;

  display: block;
  margin: 0;

  ${({ theme }) =>
    theme.sizeSelectionExpansionPanel.sizeSelectionContent.sizeButton};
`;

const SizingGrid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr 1fr 1fr 1fr;

  ${desktop`
    width: 89px;
    gap: 12px;
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
  `}
`;

/**
 * @function getNumericSizeBySizeLabel
 * @description Gets numeric size equivalent for a given size label
 * @param {Object} sizeChart Collection of sizes for the given product type
 * @param {string} sizeLabel The stringified size
 * @returns {string} The stringified numeric size for a given size
 */
export function getNumericSizeBySizeLabel({ sizeChart, sizeLabel }) {
  const sizes = Object.entries(sizeChart);

  const found = sizes
    .map(([value, size]) => ({ value, size }))
    .find((s) =>
      s.size
        .split('|')
        .map((s2) => s2.trim())
        .find((v) => v === sizeLabel)
    ).value;

  return found;
}

function SizeSelectionContent({ openNext, sizeType }) {
  const { updateSizeSelections, sizeSelectionValues, sizeChart } =
    useSizeSelectionContext();

  /**
   * @const sizeLabels
   * @description Provides a list of readable sizes
   */
  const sizeLabels = useMemo(() => {
    const useSizeChartDataAllCollections = config.get(
      'public.brand.sizeSelectionExpansionPanel.useSizeChartDataAllCollections'
    );

    const pppUseApparelSize = config.get(
      'public.brand.features.pppUseApparelSize'
    );

    if (useSizeChartDataAllCollections) {
      return Object.keys(getSizeChartDataAllCollections().US);
    }

    const categorySizeChart = pppUseApparelSize
      ? sizeChart.apparel
      : sizeChart[sizeType] || sizeChart.apparel;

    return Object.values(categorySizeChart).map((s) => {
      const [sizeText] = s.split('|');
      return sizeText.trim();
    });
  }, [sizeType, sizeChart]);

  return (
    <Collapsible.Content>
      <Content
        data-autotag={createDataAutoTagString(
          'size_selection',
          snakeCase(sizeType),
          'grid'
        )}
      >
        <SizingGrid>
          {sizeLabels.map((size, idx) => {
            return (
              <SizeBlock
                key={idx}
                $selected={
                  sizeSelectionValues[sizeType] ===
                  getNumericSizeBySizeLabel({
                    sizeChart: sizeChart[sizeType] || sizeChart.apparel,
                    sizeLabel: size,
                  })
                }
                onClick={() => {
                  const numericSize = getNumericSizeBySizeLabel({
                    sizeChart: sizeChart[sizeType] || sizeChart.apparel,
                    sizeLabel: size,
                  });

                  updateSizeSelections({
                    ...sizeSelectionValues,
                    [sizeType]: numericSize,
                  });

                  openNext();
                }}
                data-autotag={createDataAutoTagString(
                  snakeCase(sizeType),
                  size,
                  'button'
                )}
              >
                {size}
              </SizeBlock>
            );
          })}
        </SizingGrid>
      </Content>
    </Collapsible.Content>
  );
}

SizeSelectionContent.propTypes = {
  openNext: PropTypes.func,
  sizeType: PropTypes.string,
};

SizeSelectionContent.SizingGrid = SizingGrid;

export default SizeSelectionContent;
